import React from 'react';
import * as Sentry from '@sentry/nextjs';

import { getProductDetail } from 'api';
import { RequestError } from 'api/utils';
import RichText from 'components/cms/RichText';
import Container from 'components/Container';
import ImageComponent from 'components/Image';
import Spaced from 'components/Spaced';
import TaggedImage, { Tag, TaggedImageCallbacks } from 'components/TaggedImage';
import ProductTagTooltip from 'components/TaggedImage/partials/ProductTagTooltip';
import { Picture } from 'types/Picture';
import { Product } from 'types/Product';
import { makeImageSources, toast } from 'utils';
import styles from './Image.module.css';

interface ProductTag extends Tag {
  ref: number;
  productId: number;
}

export interface ImageDimension {
  height: number;
  width: number;
}

interface ImageProps {
  // these two booleans should be merged into one enum
  fullscreen: boolean;
  padded: boolean;
  tags: ProductTag[];
  image: Picture;
  text: string;
  dimensions?: ImageDimension;
}

function aspect(width?: number, height?: number, fullscreen = false) {
  if (!width || !height) {
    return undefined;
  }

  if (fullscreen) {
    return width / height;
  }

  if (width > height) {
    return 'letterbox';
  }

  return 'letterbox-portrait';
}

const Image: React.FCWithChildren<ImageProps> = props => {
  const { fullscreen, padded, tags, image, text = '', dimensions } = props;
  const { width, height } = dimensions || {};
  const MaybeContainer = padded || !fullscreen ? Container : React.Fragment;

  const getTagData = async (tag: ProductTag) => {
    try {
      const productResponse = await getProductDetail({ id: String(tag.productId) });

      return productResponse.data;
    } catch (error) {
      if (error instanceof RequestError) {
        Sentry.captureException(error);
        toast();
      }
    }
  };

  const renderTagData: TaggedImageCallbacks<Product, ProductTag>['renderTagData'] = (
    product,
    onClose,
    open,
    translateX,
  ) => {
    if (!product) return null;

    return (
      <ProductTagTooltip product={product} onClose={onClose} open={open} translateX={translateX} />
    );
  };

  return (
    <MaybeContainer noNestedPadding>
      <Spaced>
        {tags.length > 0 ? (
          <TaggedImage<Product, ProductTag>
            renderTagData={renderTagData}
            getTagData={getTagData}
            tags={tags.map(tag => ({ ...tag, ref: tag.ref }))}
            {...makeImageSources(image)}
          />
        ) : (
          <ImageComponent
            aspectRatio={aspect(width, height, fullscreen)}
            alt=""
            {...makeImageSources(image)}
          />
        )}
        {text && (
          <Container noNestedPadding>
            <div className={styles.imageText}>
              <RichText text={text} />
            </div>
          </Container>
        )}
      </Spaced>
    </MaybeContainer>
  );
};

export default Image;
